import { useRouter } from "next/navigation";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { buildPlaceDetailPath, buildPlaySearchPath } from "@/constants/routes";
import { saveLocalSearchTerm } from "./search-local-history";
import { normalizeTerm } from "./search-utils";
import { isRoutablePlaceSuggestion } from "./place-suggestion-service";

const blurActiveSearchInput = () => {
  if (typeof document === "undefined") return;

  const activeElement = document.activeElement;
  if (!activeElement || typeof activeElement.blur !== "function") return;

  const tagName = activeElement.tagName?.toLowerCase();
  const isTextEntry =
    tagName === "input" ||
    tagName === "textarea" ||
    activeElement.isContentEditable;

  if (isTextEntry) {
    activeElement.blur();
  }
};

export const useSearchActions = ({
  trimmedSearch,
  setSearch,
  setSearchInFocus,
  setMobileOverlayOpen,
  addToSearchHistory,
  onMobileOverlayClose,
}) => {
  const router = useRouter();
  const { user } = useAuthContext();

  const closeSearch = () => {
    blurActiveSearchInput();
    setSearchInFocus(false);
    setMobileOverlayOpen(false);
    onMobileOverlayClose?.();
  };

  const openSearch = () => {
    setSearchInFocus(true);
    setMobileOverlayOpen(true);
  };

  const routeToSearch = (query, params = {}) => {
    const nextSearch = normalizeTerm(query);
    if (!nextSearch) return;

    saveLocalSearchTerm(nextSearch);
    setSearch(nextSearch);
    closeSearch();
    router.push(buildPlaySearchPath(nextSearch, params));
    addToSearchHistory(nextSearch, user?.userId);
  };

  const routeToPlace = (place) => {
    const placeName = normalizeTerm(place?.name);

    if (place && typeof place === "object" && !isRoutablePlaceSuggestion(place)) {
      routeToSearch(placeName || trimmedSearch);
      return;
    }

    const placePath = buildPlaceDetailPath(place);
    const fallbackPlaceId =
      place?._id || place?.id || place?.placeUniqueNumber || place?.publicId;
    const targetPath =
      placePath && placePath !== "/places"
        ? placePath
        : fallbackPlaceId
          ? `/place/${encodeURIComponent(fallbackPlaceId)}`
          : "";

    if (!targetPath) return;

    const historyTerm = placeName || trimmedSearch;
    if (historyTerm) {
      saveLocalSearchTerm(historyTerm);
      addToSearchHistory(historyTerm, user?.userId);
    }
    setSearch(placeName || trimmedSearch);
    closeSearch();
    router.push(targetPath);
  };

  return {
    routeToSearch,
    routeToPlace,
    openSearch,
    closeSearch,
  };
};
